/**
 * brandLogo — fonte única do logo padrão da plataforma.
 *
 * Política: NÃO existe fallback SVG/Base64 embutido. Se o admin não configurou
 * `logo_principal` / `logo_admin`, o consumidor recebe `null` e deve renderizar
 * um placeholder vazio (ou texto puro), nunca um SVG genérico.
 *
 * Normalização de host: o admin pode ter salvo a logo apontando para o host do
 * preview (ex.: `https://secure-community-6.preview.emergentagent.com/api/uploads/...`)
 * mas o consumidor pode estar acessando pelo domínio custom de produção
 * (ex.: `https://empontenova.com.br`). Nesses casos reescrevemos para o
 * `REACT_APP_BACKEND_URL` corrente — assim a logo carrega corretamente em qualquer
 * ambiente sem precisar republicar no admin.
 */

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

/**
 * Quando o asset for um upload servido pelo backend (`/api/uploads/...`),
 * força o host atual. URLs externas (Unsplash, CDNs próprios) são preservadas.
 */
function rebaseBackendAsset(rawUrl) {
  if (!rawUrl || !BACKEND_URL) return rawUrl;
  try {
    const url = new URL(rawUrl);
    if (url.pathname.startsWith('/api/uploads/') || url.pathname.startsWith('/uploads/')) {
      return `${BACKEND_URL}${url.pathname}${url.search}`;
    }
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

/**
 * Resolve a URL do logo a partir da config do admin.
 *   - data: URI / URL externa → usa direto
 *   - URL absoluta apontando para /api/uploads/... → rebases pro host atual
 *   - URL relativa (/uploads/...) → prefixa com REACT_APP_BACKEND_URL
 *   - vazia / null → retorna null (consumidor decide o placeholder)
 */
export function resolveBrandLogo(config) {
  const raw = config?.logo_principal || config?.logo_admin || '';
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return rebaseBackendAsset(raw);
  }
  if (raw.startsWith('/')) {
    return `${BACKEND_URL}${raw}`;
  }
  return raw;
}

/**
 * Resolve qualquer URL de imagem servida pelo backend (útil pra Header/Footer/
 * Admin layouts) aplicando a mesma normalização de host.
 */
export function resolveBackendImage(raw) {
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return rebaseBackendAsset(raw);
  }
  if (raw.startsWith('/')) {
    return `${BACKEND_URL}${raw}`;
  }
  return raw;
}
